import { useForm, useList } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  FormField,
  FormStickyActions,
  FormHeader,
  FormSpacer,
} from '@/components/forms';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TranslationsForm,
  LanguageSwitcher,
  createEmptyTranslationData,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
  type LanguageCode,
} from '@/components/translations';
import { FileImageUpload } from '@/components/upload';
import { ContentTagsField } from '@/components/shared';
import { AlertTriangle } from 'lucide-react';
import type { ChannelPass, ChannelPassFormData } from './types';

const TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  {
    name: 'name',
    label: 'Название',
    type: 'input',
  },
  {
    name: 'description',
    label: 'Полное описание',
    type: 'richtext',
  },
];

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
};

type TelegramChannelRecord = {
  id: number;
  title: string;
  username: string | null;
};

type TriggerRecord = {
  id: number;
  name: string;
};

export function ChannelPassesForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<ChannelPass>({
    resource: 'channel-passes',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'list',
    meta: { endpoint: 'admin' },
  });

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
  });

  const { result: channelsResult } = useList<TelegramChannelRecord>({
    resource: 'telegram-channels',
    pagination: { pageSize: 100 },
  });

  const { result: triggersResult } = useList<TriggerRecord>({
    resource: 'triggers',
    pagination: { pageSize: 100 },
  });

  const languages: TranslationLanguage[] | undefined = languagesResult?.data?.map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flag || l.flagEmoji || '🏳️',
  }));

  const channels = channelsResult?.data ?? [];
  const triggers = triggersResult?.data ?? [];

  const pass = query?.data?.data;
  const initialState = buildFormState(pass);
  const formKey = isEdit ? `edit-${pass?.id ?? 'loading'}` : 'create';

  return (
    <ChannelPassesFormFields
      key={formKey}
      isEdit={isEdit}
      formLoading={formLoading}
      initialState={initialState}
      channels={channels}
      triggers={triggers}
      passId={pass?.id ?? null}
      onFinish={async (v) => {
        await onFinish(v);
      }}
      onBack={() => navigate(-1)}
      languages={languages}
    />
  );
}

export interface FormState {
  channelId: string;
  hasLimits: boolean;
  userLimit: string;
  saleEndDate: Date | null;
  onLimitAction: string;
  onLimitTriggerId: string;
  onLimitDelayDays: string;
  isActive: boolean;
  targetLanguages: string[];
  imageFile: { id: number; url: string } | null;
  bannerImageFile: { id: number; url: string } | null;
  translations: TranslationData;
}

export function buildFormState(pass?: ChannelPass): FormState {
  const baseState: FormState = {
    channelId: '',
    hasLimits: false,
    userLimit: '',
    saleEndDate: null,
    onLimitAction: 'HIDE_FROM_STORE',
    onLimitTriggerId: '',
    onLimitDelayDays: '0',
    isActive: true,
    targetLanguages: [],
    imageFile: null,
    bannerImageFile: null,
    translations: createEmptyTranslationData(),
  };

  if (!pass) {
    return baseState;
  }

  const translations = createEmptyTranslationData();
  if (pass.translations) {
    for (const [langCode, data] of Object.entries(pass.translations)) {
      if (data && typeof data === 'object') {
        const typedData = data as {
          name?: string;
          shortDescription?: string;
          description?: string;
        };
        translations[langCode as keyof TranslationData] = {
          name: typedData.name ?? '',
          shortDescription: typedData.shortDescription ?? '',
          description: typedData.description ?? '',
        };
      }
    }
  }

  const hasLimits = pass.hasLimits || Boolean(pass.userLimit) || Boolean(pass.saleEndDate);

  return {
    channelId: String(pass.channelId),
    hasLimits,
    userLimit: pass.userLimit ? String(pass.userLimit) : '',
    saleEndDate: pass.saleEndDate ? new Date(pass.saleEndDate) : null,
    onLimitAction: pass.onLimitAction,
    onLimitTriggerId: pass.onLimitTriggerId ? String(pass.onLimitTriggerId) : '',
    onLimitDelayDays: String(pass.onLimitDelayDays),
    isActive: pass.isActive,
    targetLanguages: pass.targetLanguages ?? [],
    imageFile: pass.imageId && pass.imageUrl ? { id: pass.imageId, url: pass.imageUrl } : null,
    bannerImageFile: pass.bannerImageId && pass.bannerImageUrl ? { id: pass.bannerImageId, url: pass.bannerImageUrl } : null,
    translations,
  };
}

export interface FormFieldsProps {
  isEdit: boolean;
  formLoading: boolean;
  initialState: FormState;
  channels: TelegramChannelRecord[];
  triggers: TriggerRecord[];
  passId?: number | null;
  onFinish: (values: ChannelPassFormData) => Promise<void>;
  onBack: () => void;
  languages?: TranslationLanguage[];
  /** Когда true — скрывает FormHeader и FormStickyActions (для встраивания в таб) */
  embedded?: boolean;
  /** Кастомный footer (для wizard). Получает submit-функцию и флаг загрузки */
  renderFooter?: (submit: () => void, loading: boolean) => React.ReactNode;
}

export function ChannelPassesFormFields({
  isEdit,
  formLoading,
  initialState,
  channels,
  triggers,
  passId = null,
  onFinish,
  onBack,
  languages,
  embedded = false,
  renderFooter,
}: FormFieldsProps) {
  const [channelId, setChannelId] = useState(initialState.channelId);
  const [imageFile, setImageFile] = useState(initialState.imageFile);
  const [bannerImageFile, setBannerImageFile] = useState(initialState.bannerImageFile);

  const [hasLimits, setHasLimits] = useState(initialState.hasLimits);
  const [userLimit, setUserLimit] = useState(initialState.userLimit);
  const [saleEndDate, setSaleEndDate] = useState<Date | null>(initialState.saleEndDate);
  const [onLimitAction, setOnLimitAction] = useState(initialState.onLimitAction);
  const [onLimitTriggerId, setOnLimitTriggerId] = useState(initialState.onLimitTriggerId);
  const [onLimitDelayDays, setOnLimitDelayDays] = useState(initialState.onLimitDelayDays);

  const [isActive, setIsActive] = useState(initialState.isActive);
  const [targetLanguages, setTargetLanguages] = useState(initialState.targetLanguages);
  const [translations, setTranslations] = useState<TranslationData>(initialState.translations);
  const [activeLanguage, setActiveLanguage] = useState<LanguageCode>('ru');

  const handleSubmit = async () => {
    const formData: ChannelPassFormData = {
      channelId: Number(channelId),
      hasLimits,
      userLimit: hasLimits && userLimit ? Number(userLimit) : null,
      saleEndDate: hasLimits && saleEndDate ? saleEndDate.toISOString() : null,
      onLimitAction: (hasLimits ? onLimitAction : 'HIDE_FROM_STORE') as
        | 'HIDE_FROM_STORE'
        | 'DEACTIVATE'
        | 'TRIGGER',
      onLimitTriggerId: hasLimits && onLimitTriggerId ? Number(onLimitTriggerId) : null,
      onLimitDelayDays: hasLimits ? Number(onLimitDelayDays) || 0 : 0,
      isActive,
      targetLanguages,
      imageId: imageFile?.id ?? null,
      bannerImageId: bannerImageFile?.id ?? null,
      translations: Object.entries(translations)
        .filter(([_, data]) => data?.name)
        .map(([code, data]) => ({
          languageCode: code,
          name: data?.name ?? '',
          shortDescription: data?.shortDescription ?? null,
          description: data?.description ?? null,
        })),
    };

    await onFinish(formData);
  };

  const hasAnyLimitCondition = Boolean(userLimit) || Boolean(saleEndDate);

  return (
    <div className="space-y-6">
      {!embedded && (
        <FormHeader
          title={isEdit ? 'Редактировать доступ' : 'Создать доступ'}
          onBack={onBack}
          sticky
          actions={
            languages && languages.length > 0 ? (
              <LanguageSwitcher
                languages={languages}
                activeLanguage={activeLanguage}
                onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
                fields={TRANSLATION_FIELDS}
                value={translations}
                variant="compact"
              />
            ) : null
          }
        />
      )}

      {embedded && languages && languages.length > 0 && (
        <div className="flex justify-end">
          <LanguageSwitcher
            languages={languages}
            activeLanguage={activeLanguage}
            onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
            fields={TRANSLATION_FIELDS}
            value={translations}
            variant="compact"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Основные настройки</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Канал" name="channelId" required>
                <Select value={channelId} onValueChange={setChannelId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите канал" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((ch) => (
                      <SelectItem key={ch.id} value={String(ch.id)}>
                        {ch.title} {ch.username ? `(@${ch.username})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Обложка (1:1)" name="imageId" hint="Отображается в списке каналов">
                <FileImageUpload
                  value={imageFile}
                  onChange={setImageFile}
                  aspectRatio="1/1"
                  placeholder="Рекомендуемое соотношение сторон 1:1"
                />
              </FormField>

              <FormField label="Баннер (16:9)" name="bannerImageId" hint="Отображается на странице канала">
                <FileImageUpload
                  value={bannerImageFile}
                  onChange={setBannerImageFile}
                  aspectRatio="16/9"
                  placeholder="Рекомендуемое соотношение сторон 16:9"
                />
              </FormField>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Название и описание</CardTitle>
            </CardHeader>
            <CardContent>
              <TranslationsForm
                fields={TRANSLATION_FIELDS}
                value={translations}
                onChange={setTranslations}
                languages={languages}
                activeLanguage={activeLanguage}
                onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Ограничения продаж</CardTitle>
                <div className="flex items-center gap-2">
                  <Switch id="hasLimits" checked={hasLimits} onCheckedChange={setHasLimits} />
                  <Label htmlFor="hasLimits" className="text-sm font-normal">
                    {hasLimits ? 'Включены' : 'Выключены'}
                  </Label>
                </div>
              </div>
            </CardHeader>
            {hasLimits && (
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="text-muted-foreground text-sm font-medium">
                    Условия ограничения (можно указать оба одновременно)
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      label="Лимит покупок"
                      name="userLimit"
                      hint="Максимальное количество покупок"
                    >
                      <Input
                        type="number"
                        value={userLimit}
                        onChange={(e) => setUserLimit(e.target.value)}
                        placeholder="100"
                      />
                    </FormField>

                    <FormField label="Продавать до" name="saleEndDate" hint="Дата окончания продаж">
                      <DatePicker
                        value={saleEndDate}
                        onChange={setSaleEndDate}
                        placeholder="Выберите дату"
                      />
                    </FormField>
                  </div>

                  {!hasAnyLimitCondition && (
                    <div className="bg-warning/10 border-warning/20 text-warning flex items-center gap-2 rounded-lg border p-3 text-sm">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span>Укажите хотя бы одно условие: лимит покупок или дату окончания</span>
                    </div>
                  )}
                </div>

                {hasAnyLimitCondition && (
                  <div className="space-y-4 border-t pt-4">
                    <div className="text-muted-foreground text-sm font-medium">
                      Действие при достижении ограничения
                    </div>

                    <FormField label="Действие" name="onLimitAction">
                      <Select value={onLimitAction} onValueChange={setOnLimitAction}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HIDE_FROM_STORE">Скрыть из магазина</SelectItem>
                          <SelectItem value="DEACTIVATE">Деактивировать</SelectItem>
                          <SelectItem value="TRIGGER">Запустить триггер</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>

                    {onLimitAction === 'TRIGGER' && (
                      <FormField label="Триггер" name="onLimitTriggerId">
                        <Select value={onLimitTriggerId} onValueChange={setOnLimitTriggerId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Выберите триггер" />
                          </SelectTrigger>
                          <SelectContent>
                            {triggers.map((t) => (
                              <SelectItem key={t.id} value={String(t.id)}>
                                {t.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                    )}

                    <FormField
                      label="Задержка действия (дней)"
                      name="onLimitDelayDays"
                      hint="Через сколько дней после достижения лимита выполнить действие"
                    >
                      <Input
                        type="number"
                        value={onLimitDelayDays}
                        onChange={(e) => setOnLimitDelayDays(e.target.value)}
                        placeholder="0"
                      />
                    </FormField>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Публикация</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={(checked) => setIsActive(checked === true)}
                />
                <label htmlFor="isActive" className="text-sm leading-none font-medium">
                  Активна
                </label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Аудитория</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                label="Видимость по языкам аудитории"
                name="targetLanguages"
                hint="Пустое = виден всем. Выберите языки — канал будет показан только пользователям с этими языками."
              >
                <MultiSelect
                  options={(languages ?? []).map((l) => ({ label: l.name, value: l.code }))}
                  value={targetLanguages}
                  onChange={setTargetLanguages}
                  placeholder="Виден всем"
                  searchPlaceholder="Поиск языка..."
                  emptyMessage="Языки не найдены"
                />
              </FormField>
              <ContentTagsField entityType="CHANNEL_PASS" entityId={passId} />
            </CardContent>
          </Card>
        </div>
      </div>

      {renderFooter
        ? renderFooter(() => { void handleSubmit(); }, formLoading)
        : null}

      {!renderFooter && embedded && (
        <div className="flex justify-end gap-2 pt-4">
          <Button onClick={handleSubmit} disabled={formLoading || !channelId}>
            {formLoading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      )}

      {!renderFooter && !embedded && (
        <>
          <FormSpacer />
          <FormStickyActions>
            <Button variant="outline" onClick={onBack} disabled={formLoading} type="button">
              Отмена
            </Button>
            <Button onClick={handleSubmit} disabled={formLoading || !channelId}>
              {formLoading ? 'Сохранение...' : (isEdit ? 'Сохранить' : 'Создать')}
            </Button>
          </FormStickyActions>
        </>
      )}
    </div>
  );
}
